import PageTitle from '@/components/PageTitle';
import ThemeWrapper from '@/components/ThemeWrapper';
import useApi from '@/hooks/useApi';
import AppLayout from '@/layouts/AppLayout';
import { router } from '@inertiajs/react';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface SearchResult {
    id: number;
    title: string;
    type: 'course' | 'class';
    course_title?: string;
    url: string;
}

interface Pagination {
    page: number;
    per_page: number;
    total: number;
    has_more: boolean;
    next_page: number | null;
}

interface Props {
    query: string;
    results: SearchResult[];
    pagination: Pagination;
}

const ResultsContent: React.FC<Props> = ({ query, results, pagination }) => {
    const { t } = useTranslation();
    const api = useApi();
    const [searchQuery, setSearchQuery] = useState(query || '');
    const [items, setItems] = useState<SearchResult[]>(results || []);
    const [pageInfo, setPageInfo] = useState<Pagination>(pagination);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const sentinelRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        setSearchQuery(query || '');
        setItems(results || []);
        setPageInfo(pagination);
    }, [query, results, pagination]);

    useEffect(() => {
        if (!sentinelRef.current || !pageInfo.has_more) {
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                if (
                    entry.isIntersecting &&
                    !isLoadingMore &&
                    pageInfo.has_more &&
                    pageInfo.next_page
                ) {
                    setIsLoadingMore(true);
                    api.get('/courses/search/results/', {
                        params: {
                            q: searchQuery.trim(),
                            page: pageInfo.next_page,
                        },
                    })
                        .then((response) => {
                            const nextResults = response.data.results || [];
                            setItems((prev) => [...prev, ...nextResults]);
                            setPageInfo(response.data.pagination);
                        })
                        .finally(() => {
                            setIsLoadingMore(false);
                        });
                }
            },
            { rootMargin: '120px' }
        );

        observer.observe(sentinelRef.current);

        return () => observer.disconnect();
    }, [api, isLoadingMore, pageInfo, searchQuery]);

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        router.get(`/courses/search/?q=${encodeURIComponent(searchQuery.trim())}`);
    };

    return (
        <AppLayout>
            <PageTitle title="Resultados de Busqueda" />
            <div className="mx-auto max-w-5xl space-y-6">
                <div className="space-y-2">
                    <h1 className="text-2xl font-semibold text-gray-900">
                        {t('search.resultsTitle')}
                    </h1>
                    <form onSubmit={handleSubmit} className="relative">
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(event) =>
                                setSearchQuery(event.target.value)
                            }
                            placeholder={t('search.placeholder')}
                            className="input w-full"
                        />
                    </form>
                    {searchQuery && (
                        <p className="text-sm text-gray-500">
                            {t('search.resultsCount', {
                                count: pageInfo.total,
                            })}
                        </p>
                    )}
                </div>

                {items.length === 0 ? (
                    <div className="rounded-lg border border-dashed border-gray-300 bg-white p-8 text-center text-gray-500">
                        {t('search.noResults')}
                    </div>
                ) : (
                    <div className="space-y-3">
                        {items.map((result) => (
                            <button
                                key={`${result.type}-${result.id}`}
                                type="button"
                                onClick={() => router.get(result.url)}
                                className="flex w-full flex-col gap-1 rounded-lg border border-gray-200 bg-white px-4 py-3 text-left transition hover:border-cb-border hover:bg-cb-light"
                            >
                                <div className="flex items-center justify-between">
                                    <span className="text-base font-medium text-gray-900">
                                        {result.title}
                                    </span>
                                    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                                        {result.type === 'course'
                                            ? t('search.types.course')
                                            : t('search.types.class')}
                                    </span>
                                </div>
                                {result.type === 'class' &&
                                    result.course_title && (
                                        <span className="text-sm text-gray-500">
                                            {result.course_title}
                                        </span>
                                    )}
                            </button>
                        ))}
                        <div ref={sentinelRef} />
                        {isLoadingMore && (
                            <div className="text-center text-sm text-gray-500">
                                {t('search.loading')}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </AppLayout>
    );
};

const Results: React.FC<Props> = ({ query, results, pagination }) => {
    return (
        <ThemeWrapper>
            <ResultsContent
                query={query}
                results={results}
                pagination={pagination}
            />
        </ThemeWrapper>
    );
};

export default Results;
